import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
// import Modal from './Modal'; // Assuming you have a Modal component
import { format } from 'date-fns';
import * as XLSX from 'xlsx'; // For Excel file handling
import { XIcon } from '@heroicons/react/outline';

import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 5000,
});
const Modal = ({ isOpen, onClose, title, children }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'
      onClick={handleBackdropClick}
      aria-modal='true'
      role='dialog'
      aria-labelledby='modal-title'
    >
      <div className='bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] flex flex-col'>
        <div className='flex justify-between items-center p-4 border-b'>
          <h2 id='modal-title' className='text-xl font-semibold'>
            {title}
          </h2>
          <button
            onClick={onClose}
            className='text-gray-400 hover:text-gray-600 transition-colors'
            aria-label='Close modal'
          >
            <XIcon className='h-6 w-6' />
          </button>
        </div>

        <div className='overflow-y-auto'>{children}</div>
      </div>
    </div>
  );
};

const SubscriberListTwo = () => {
  const { restaurantId } = useParams();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalSubscribers: 0,
  });

  useEffect(() => {
    fetchSubscribers();
  }, [pagination.currentPage, searchPhone, dateRange]);

  const fetchSubscribers = async () => {
    try {
      const token = localStorage.getItem('ownerToken');

      setLoading(true);
      const queryParams = new URLSearchParams({
        page: pagination.currentPage,
        searchPhone: searchPhone,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });

      const response = await api.get(
        `/restaurants/${restaurantId}/subscribers?${queryParams}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      ); //           `/api/restaurants/${restaurantId}/subscribers`,

      setSubscribers(response.data.subscribers);
      setPagination({
        currentPage: response.data.currentPage,
        totalPages: response.data.totalPages,
        totalSubscribers: response.data.totalSubscribers,
      });
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      toast.error('Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubscriber = async () => {
    try {
      const token = localStorage.getItem('ownerToken');

      await api.post(
        `/restaurants/${restaurantId}/add`,
        {
          phoneNumber: phoneNumber,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Subscriber added successfully');
      setShowAddModal(false);
      setPhoneNumber('');
      fetchSubscribers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add subscriber');
    }
  };

  const handleBulkImport = async (file) => {
    try {
      const token = localStorage.getItem('ownerToken');

      const reader = new FileReader();
      reader.onload = async (e) => {
        const workbook = XLSX.read(e.target.result, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(worksheet);

        // Validate and format phone numbers
        const phoneNumbers = data
          .map((row) => {
            const phone =
              row.phone || row.phoneNumber || row['Phone Number'] || '';
            return phone.toString().replace(/\D/g, '');
          })
          .filter((phone) => phone.length >= 10);

        if (phoneNumbers.length === 0) {
          toast.error('No valid phone numbers found in file');
          return;
        }

        await api.post(
          `/restaurants/${restaurantId}/bulk-add`,
          {
            phoneNumbers,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        toast.success(
          `Successfully imported ${phoneNumbers.length} subscribers`
        );
        setShowBulkModal(false);
        fetchSubscribers();
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      toast.error('Failed to import subscribers');
    }
  };

  const handleUnsubscribe = async (customerId) => {
    try {
      const token = localStorage.getItem('ownerToken');

      await axios.delete(
        `/api/restaurants/${restaurantId}/subscribers/${customerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSubscribers(
        subscribers.filter((customer) => customer._id !== customerId)
      );
    } catch (err) {
      // setError('Failed to delete customer.');
      console.log(err);
    }
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([{ 'Phone Number': '1234567890' }]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Subscribers');
    XLSX.writeFile(wb, 'subscriber_template.xlsx');
  };

  return (
    <div className='container mx-auto px-4 py-8'>
      <div className='mb-6 flex justify-between items-center'>
        <h2 className='text-2xl font-bold'>Subscribers</h2>
        <div className='space-x-4'>
          <button
            onClick={() => setShowAddModal(true)}
            className='bg-blue-500 text-white px-4 py-2 rounded'
          >
            Add Subscriber
          </button>
          <button
            onClick={() => setShowBulkModal(true)}
            className='bg-green-500 text-white px-4 py-2 rounded'
          >
            Bulk Import
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className='mb-6 grid grid-cols-1 md:grid-cols-3 gap-4'>
        <input
          type='text'
          placeholder='Search by phone number'
          value={searchPhone}
          onChange={(e) => setSearchPhone(e.target.value)}
          className='border p-2 rounded'
        />
        <input
          type='date'
          value={dateRange.startDate}
          onChange={(e) =>
            setDateRange((prev) => ({ ...prev, startDate: e.target.value }))
          }
          className='border p-2 rounded'
        />
        <input
          type='date'
          value={dateRange.endDate}
          onChange={(e) =>
            setDateRange((prev) => ({ ...prev, endDate: e.target.value }))
          }
          className='border p-2 rounded'
        />
      </div>

      {/* Subscribers Table */}
      <div className='bg-white shadow overflow-hidden rounded-lg'>
        <table className='min-w-full divide-y divide-gray-200'>
          <thead className='bg-gray-50'>
            <tr>
              <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Phone Number
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Subscribed Date
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Status
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className='bg-white divide-y divide-gray-200'>
            {subscribers.map((subscriber) => (
              <tr key={subscriber._id}>
                <td className='px-6 py-4 whitespace-nowrap'>
                  {subscriber.customer.phoneNumber}
                </td>
                <td className='px-6 py-4 whitespace-nowrap'>
                  {format(new Date(subscriber.subscribedAt), 'MMM dd, yyyy')}
                </td>
                <td className='px-6 py-4 whitespace-nowrap'>
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${
                      subscriber.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {subscriber.status}
                  </span>
                </td>
                <td className='px-6 py-4 whitespace-nowrap'>
                  <button
                    onClick={() => handleUnsubscribe(subscriber._id)}
                    className='text-red-600 hover:text-red-900'
                  >
                    Unsubscribe
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className='mt-4 flex justify-between items-center'>
        <button
          onClick={() =>
            setPagination((prev) => ({
              ...prev,
              currentPage: prev.currentPage - 1,
            }))
          }
          disabled={pagination.currentPage === 1}
          className='px-4 py-2 border rounded disabled:opacity-50'
        >
          Previous
        </button>
        <span>
          Page {pagination.currentPage} of {pagination.totalPages}
        </span>
        <button
          onClick={() =>
            setPagination((prev) => ({
              ...prev,
              currentPage: prev.currentPage + 1,
            }))
          }
          disabled={pagination.currentPage === pagination.totalPages}
          className='px-4 py-2 border rounded disabled:opacity-50'
        >
          Next
        </button>
      </div>

      {/* Add Subscriber Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title='Add New Subscriber'
      >
        <div className='p-4'>
          <input
            type='text'
            placeholder='Enter phone number'
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            className='w-full border p-2 rounded mb-4'
          />
          <button
            onClick={handleAddSubscriber}
            className='w-full bg-blue-500 text-white px-4 py-2 rounded'
          >
            Add Subscriber
          </button>
        </div>
      </Modal>

      {/* Bulk Import Modal */}
      <Modal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        title='Bulk Import Subscribers'
      >
        <div className='p-4'>
          <div className='mb-4'>
            <p className='text-sm text-gray-600 mb-2'>
              Upload an Excel file with phone numbers or use our template.
            </p>
            <button
              onClick={downloadTemplate}
              className='text-blue-500 hover:text-blue-700 text-sm'
            >
              Download Template
            </button>
          </div>
          <input
            type='file'
            accept='.xlsx,.xls,.csv'
            onChange={(e) => handleBulkImport(e.target.files[0])}
            className='w-full mb-4'
          />
          <p className='text-xs text-gray-500'>
            Supported formats: .xlsx, .xls, .csv
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default SubscriberListTwo;
// import React, { useEffect, useState } from 'react';
// import { useParams } from 'react-router-dom';
// import axios from 'axios';

// const SubscriberListTwo = () => {
//   const [customers, setCustomers] = useState([]);
//   const [subscribers, setSubscribers] = useState([]);

//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const { restaurantId } = useParams();

//   useEffect(() => {
//     const fetchSubscribers = async () => {
//       try {
//         const token = localStorage.getItem('ownerToken');
//         const response = await axios.get(
//           `/api/restaurants/${restaurantId}/subscribers`,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`,
//             },
//           }
//         );
//         // console.log(response.data.subscribers);
//         setCustomers(response.data.customers);
//         setSubscribers(response.data.subscribers);
//       } catch (err) {
//         setError('Failed to load customers.');
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchSubscribers();
//   }, [restaurantId]);

//   const handleDeleteCustomer = async (customerId) => {
//     try {
//       const token = localStorage.getItem('ownerToken');

//       await axios.delete(
//         `/api/restaurants/${restaurantId}/subscribers/${customerId}`,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//           },
//         }
//       );
//       setCustomers(customers.filter((customer) => customer._id !== customerId));
//     } catch (err) {
//       setError('Failed to delete customer.');
//     }
//   };

//   //   if (loading) return <LoadingSpinner />;
//   if (error) return <div className='text-red-500'>{error}</div>;

//   return (
//     <div className='container mx-auto px-4 py-8'>
//       <h2 className='text-2xl font-bold mb-4'>Customers</h2>
//       {customers.length === 0 ? (
//         <p>No customers found.</p>
//       ) : (
//         <ul className='divide-y divide-gray-200'>
//           {/* {customers.map((customer, i) => (
//             <li
//               key={customer._id}
//               className='py-4 flex justify-between items-center'
//             >
//               <div>
//                 <p className='font-semibold'>{customer.phoneNumber}</p>
//                 <p className='text-sm text-gray-500'>
//                   Subscribed:{' '}
//                   {new Date(customer.createdAt).toLocaleDateString()}

//                 </p>
//               </div>
//               <button
//                 onClick={() => handleDeleteCustomer(customer._id)}
//                 className='bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded'
//               >
//                 Delete
//               </button>
//             </li>
//           ))} */}
//           {subscribers.map((subscriber, i) => (
//             <li
//               key={subscriber._id}
//               className=' flex justify-between items-center bg-white p-4 '
//             >
//               <div>
//                 <p className='font-semibold'>
//                   {subscriber.customer.phoneNumber}
//                 </p>
//                 <p className='text-sm text-gray-500'>
//                   Subscribed Date:{' '}
//                   {/* {new Date(customer.createdAt).toLocaleDateString()} */}
//                   {new Date(subscriber.subscribedAt).toLocaleDateString()}
//                 </p>
//               </div>
//               <button
//                 onClick={() => handleDeleteCustomer(subscriber.customer._id)}
//                 className='bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded'
//               >
//                 Delete
//               </button>
//             </li>
//           ))}
//         </ul>
//       )}
//     </div>
//   );
// };

// export default SubscriberListTwo;
